import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { clsx } from 'clsx';
import {
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastMessage {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
}

interface ToastProps {
  toast: ToastMessage;
  onClose: (id: string) => void;
}

const Toast: React.FC<ToastProps> = ({ toast, onClose }) => {
  useEffect(() => {
    if (toast.duration && toast.duration > 0) {
      const timer = setTimeout(() => {
        onClose(toast.id);
      }, toast.duration);

      return () => clearTimeout(timer);
    }
  }, [toast, onClose]);

  const icons = {
    success: CheckCircleIcon,
    error: XCircleIcon,
    warning: ExclamationTriangleIcon,
    info: InformationCircleIcon,
  };

  const colors = {
    success: 'bg-green-50 text-green-800 border-green-200',
    error: 'bg-red-50 text-red-800 border-red-200',
    warning: 'bg-yellow-50 text-yellow-800 border-yellow-200',
    info: 'bg-blue-50 text-blue-800 border-blue-200',
  };

  const iconColors = {
    success: 'text-green-400',
    error: 'text-red-400',
    warning: 'text-yellow-400',
    info: 'text-blue-400',
  };

  const Icon = icons[toast.type];

  return (
    <div
      className={clsx(
        'pointer-events-auto w-full max-w-md overflow-hidden rounded-lg border shadow-lg',
        colors[toast.type]
      )}
    >
      <div className="p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <Icon className={clsx('h-6 w-6', iconColors[toast.type])} />
          </div>
          <div className="ml-3 flex-1 pt-0.5">
            <p className="text-sm font-medium whitespace-nowrap">{toast.title}</p>
            {toast.message && (
              <p className="mt-1 text-sm opacity-90">{toast.message}</p>
            )}
          </div>
          <div className="ml-4 flex flex-shrink-0">
            <button
              type="button"
              className={clsx(
                'inline-flex rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2',
                toast.type === 'success' && 'text-green-500 hover:text-green-600 focus:ring-green-500',
                toast.type === 'error' && 'text-red-500 hover:text-red-600 focus:ring-red-500',
                toast.type === 'warning' && 'text-yellow-500 hover:text-yellow-600 focus:ring-yellow-500',
                toast.type === 'info' && 'text-blue-500 hover:text-blue-600 focus:ring-blue-500'
              )}
              onClick={() => onClose(toast.id)}
            >
              <span className="sr-only">Close</span>
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const ToastContainer: React.FC = () => {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  useEffect(() => {
    const handleToast = (event: CustomEvent<ToastMessage>) => {
      setToasts((prev) => [...prev, event.detail]);
    };

    window.addEventListener('show-toast' as any, handleToast);
    return () => {
      window.removeEventListener('show-toast' as any, handleToast);
    };
  }, []);

  const removeToast = (id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  };

  if (toasts.length === 0) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex flex-col items-end justify-start pointer-events-none p-4 sm:p-6">
      <div className="flex flex-col items-end space-y-4 mt-16">
        {toasts.map((toast) => (
          <Toast key={toast.id} toast={toast} onClose={removeToast} />
        ))}
      </div>
    </div>,
    document.body
  );
};

// Toast utility functions
export const toast = {
  show: (type: ToastType, title: string, message?: string, duration = 5000) => {
    const toastMessage: ToastMessage = {
      id: `${Date.now()}-${Math.random()}`,
      type,
      title,
      message,
      duration,
    };

    window.dispatchEvent(
      new CustomEvent('show-toast', { detail: toastMessage })
    );
  },
  success: (title: string, message?: string, duration?: number) =>
    toast.show('success', title, message, duration),
  error: (title: string, message?: string, duration?: number) =>
    toast.show('error', title, message, duration),
  warning: (title: string, message?: string, duration?: number) =>
    toast.show('warning', title, message, duration),
  info: (title: string, message?: string, duration?: number) =>
    toast.show('info', title, message, duration),
};